/**
 * StepSessionLive - Étape 5 : Séance HIIT LIVE avec minuteur intelligent
 */

import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Pause, Play, SkipForward, SkipBack, Square, Trophy, Flame
} from 'lucide-react';
import { useHIITTimer } from '../../hooks/useHIITTimer';
import { ExerciseViewer } from '../ExerciseViewer';
import { Confetti } from '../Confetti';

export function StepSessionLive({ session, exercises, onComplete, onAbandon }) {
  const handleTimerComplete = useCallback((data) => {
    onComplete(data);
  }, [onComplete]);

  const timer = useHIITTimer({
    exercises,
    workTime: session.work,
    restTime: session.rest,
    onComplete: handleTimerComplete,
  });

  const phaseColors = {
    ready: 'from-zinc-700 to-zinc-600',
    work: 'from-red-600 to-orange-500',
    rest: 'from-blue-600 to-cyan-500',
    complete: 'from-emerald-600 to-green-500',
  };

  const phaseBg = {
    ready: 'bg-zinc-900',
    work: 'bg-red-950/50',
    rest: 'bg-blue-950/50',
    complete: 'bg-emerald-950/50',
  };

  const phaseLabels = {
    ready: 'PRÊT ?',
    work: 'TRAVAIL',
    rest: 'REPOS',
    complete: 'TERMINÉ !',
  };

  const maxTime = timer.phase === 'work' ? session.work : session.rest;
  const progressPercent = maxTime > 0 ? ((maxTime - timer.timeLeft) / maxTime) * 100 : 0;

  // Not started yet
  if (timer.phase === 'ready' && !timer.isRunning) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center px-4 py-12 space-y-8"
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500 to-red-500
            flex items-center justify-center shadow-2xl shadow-orange-500/30"
        >
          <Flame className="w-16 h-16 text-white" />
        </motion.div>

        <div className="text-center">
          <p className="text-2xl font-bold text-white">Prêt(e) ?</p>
          <p className="text-zinc-400 mt-1">
            {exercises.length} exercices · {session.work}s travail / {session.rest}s repos
          </p>
        </div>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={timer.start}
          className="w-full max-w-xs py-5 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500
            text-white font-bold text-xl shadow-xl shadow-orange-500/30
            flex items-center justify-center gap-3"
        >
          <Play className="w-7 h-7" fill="white" />
          GO !
        </motion.button>

        <button
          onClick={onAbandon}
          className="text-sm text-zinc-500 hover:text-zinc-300"
        >
          Annuler
        </button>
      </motion.div>
    );
  }

  // Completed
  if (timer.phase === 'complete') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center px-4 py-8 space-y-6"
      >
        <Confetti active={true} />

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
          transition={{ type: 'spring' }}
          className="text-7xl"
        >
          🏆
        </motion.div>

        <div className="text-center">
          <h3 className="text-2xl font-black text-transparent bg-clip-text
            bg-gradient-to-r from-orange-400 to-yellow-400">
            SÉANCE TERMINÉE !
          </h3>
          <p className="text-zinc-400 mt-1">Incroyable, tu as tout donné !</p>
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{timer.formatTime(timer.totalElapsed)}</p>
            <p className="text-[10px] text-zinc-500">Durée</p>
          </div>
          <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white">{timer.completedCycles}/{timer.totalCycles}</p>
            <p className="text-[10px] text-zinc-500">Cycles</p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Active session
  return (
    <div className={`min-h-[500px] flex flex-col transition-colors duration-500 ${phaseBg[timer.phase]}`}>
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Cycle <span className="text-white font-bold">{timer.currentCycle + 1}</span> / {timer.totalCycles}
        </div>
        <div className="text-sm text-zinc-400">
          {timer.formatTime(timer.totalElapsed)}
        </div>
      </div>

      {/* Progress bar globale */}
      <div className="px-4 mb-4">
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full"
            animate={{ width: `${timer.progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Zone principale */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 space-y-6">
        {/* Exercise animation + name */}
        <AnimatePresence mode="wait">
          <motion.div
            key={timer.currentExercise?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center flex flex-col items-center"
          >
            {/* Animation du mouvement */}
            {timer.currentExercise && (
              <div className="w-full max-w-xs" style={{ height: 320 }}>
                <ExerciseViewer
                  exerciseId={timer.currentExercise.id}
                  className="w-full h-full"
                  height={320}
                  paused={timer.isPaused}
                  speed={timer.phase === 'work' ? 1 : 0.3}
                  showControls
                />
              </div>
            )}
            <p className="text-3xl font-black text-white leading-tight">
              {timer.currentExercise?.name || 'Préparation'}
            </p>
            {timer.currentExercise?.tip && (
              <p className="text-sm text-zinc-400 mt-2">{timer.currentExercise.tip}</p>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Timer circle */}
        <div className="relative w-48 h-48">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
            <circle
              cx="60" cy="60" r="54" fill="none"
              stroke="url(#timerGradient)" strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 54}`}
              strokeDashoffset={`${2 * Math.PI * 54 * (1 - progressPercent / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.3s ease' }}
            />
            <defs>
              <linearGradient id="timerGradient">
                <stop offset="0%" stopColor={timer.phase === 'work' ? '#ef4444' : '#3b82f6'} />
                <stop offset="100%" stopColor={timer.phase === 'work' ? '#f97316' : '#06b6d4'} />
              </linearGradient>
            </defs>
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.p
              key={timer.timeLeft}
              initial={{ scale: timer.timeLeft <= 3 ? 1.3 : 1 }}
              animate={{ scale: 1 }}
              className={`text-5xl font-black ${timer.timeLeft <= 3 ? 'text-yellow-400' : 'text-white'}`}
            >
              {timer.timeLeft}
            </motion.p>
            <p className={`text-sm font-bold mt-1 bg-gradient-to-r ${phaseColors[timer.phase]}
              bg-clip-text text-transparent`}>
              {phaseLabels[timer.phase]}
            </p>
          </div>
        </div>

        {/* Encouragement */}
        <AnimatePresence>
          {timer.encouragement && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-sm text-yellow-400 font-medium text-center"
            >
              {timer.encouragement}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Milestone */}
        <AnimatePresence>
          {timer.milestoneReached && timer.completedCycles === Math.ceil(timer.totalCycles / 2) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 bg-yellow-500/20 text-yellow-300
                px-4 py-2 rounded-full border border-yellow-500/30"
            >
              <Trophy className="w-4 h-4" />
              <span className="text-sm font-bold">Mi-chemin !</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Next exercise */}
      {timer.nextExercise && (
        <div className="px-4 py-2 bg-zinc-800/40">
          <p className="text-xs text-zinc-500">
            Prochain : <span className="text-zinc-300 font-medium">{timer.nextExercise.name}</span>
            {timer.phase === 'work' && <span> · {session.rest}s repos</span>}
          </p>
        </div>
      )}

      {/* Controls */}
      <div className="px-4 py-4 flex items-center justify-center gap-4">
        <button
          onClick={timer.prevExercise}
          disabled={timer.currentCycle === 0}
          className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700
            flex items-center justify-center text-zinc-400 hover:text-white
            disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <SkipBack className="w-5 h-5" />
        </button>

        <button
          onClick={timer.isPaused ? timer.resume : timer.pause}
          className={`w-16 h-16 rounded-full flex items-center justify-center
            shadow-lg transition-all bg-gradient-to-r ${phaseColors[timer.phase]}`}
        >
          {timer.isPaused
            ? <Play className="w-7 h-7 text-white ml-0.5" fill="white" />
            : <Pause className="w-7 h-7 text-white" />
          }
        </button>

        <button
          onClick={timer.skipExercise}
          className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700
            flex items-center justify-center text-zinc-400 hover:text-white transition-all"
        >
          <SkipForward className="w-5 h-5" />
        </button>
      </div>

      {/* Abandon */}
      {timer.isPaused && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="px-4 pb-4 flex justify-center"
        >
          <button
            onClick={() => { timer.stop(); onAbandon(); }}
            className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300"
          >
            <Square className="w-4 h-4" />
            Arrêter la séance
          </button>
        </motion.div>
      )}
    </div>
  );
}
